import { graphql } from '@packages/gql/generated/shopping';
import { useSession } from '@packages/utilities';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../helpers/useRecoElement';
import type { ProductsRecoSliderProps, RdeType } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoProductCard } from '../../RecoProductCard';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';
import { RecoProductCardSkeleton } from '../../RecoProductCardSkeleton';

export type LastSeenProductsRecoSliderProps = Omit<ProductsRecoSliderProps, 'preferTitle'>;

const LastSeenProductsDocument = graphql(/* GraphQL */ `
  query LastSeenProducts {
    lastSeenProducts {
      type
      recommendations {
        ...ProductRecommendationFragment
        product {
          akl
        }
      }
    }
  }
`);

const RDE_TYPE: RdeType = 'lastSeenProducts';

/**
 * PrudsysProductsRecoSlider component description displayed in storybook
 * */
export const LastSeenProductsRecoSlider: FC<LastSeenProductsRecoSliderProps> = ({
  elementIndex = 0,
  fetchOnThreshold = false,
  pageTemplate,
  title,
}) => {
  const { jwtToken, loading } = useSession();
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: LastSeenProductsDocument,
    variables: {},
    context: useMemo(
      () => ({ suspense: false, fetchOptions: { headers: { 'x-ec-token': jwtToken! } } }),
      [jwtToken],
    ),
    pause: (fetchOnThreshold ? !isInThreshold : false) || loading || !jwtToken,
  });

  const recommendations = data ? data.lastSeenProducts.recommendations : undefined;

  const rdeTypeAbbr = data?.lastSeenProducts.type;

  useProductsRecoTracking({
    elementIndex,
    pageTemplate,
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations,
    title,
    trackingCondition: isInView && !!recommendations && recommendations.length > 0,
  });

  if ((error && !recommendations) || (recommendations && recommendations.length === 0)) {
    return null;
  }

  return (
    <RecoCarousel title={title} ref={recoEl} loadingState={!recommendations}>
      {!recommendations
        ? // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          [...Array(3)].map((_, i) => <RecoProductCardSkeleton key={i} />)
        : recommendations
            .filter((recommendation) => recommendation.product)
            .map((recommendation, idx) => (
              <RecoProductCard
                pageTemplate={pageTemplate}
                // rule is disabled because order doesn't change between rerenders
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                elementIndex={elementIndex}
                position={idx}
                recommendationData={recommendation}
                productsLength={recommendations.length}
                rdeType={RDE_TYPE}
              />
            ))}
    </RecoCarousel>
  );
};
